import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, ArrowRight, Clock, Image, Video, Trophy } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { OptimizedImage } from '../components/OptimizedImage';
import AlbumGallery from '../components/actualites/AlbumGallery';
import NewsletterSection from '../components/actualites/NewsletterSection';
import NewsModal from '../components/actualites/NewsModal';

const API = `${window.location.origin}/api`;

export default function Actualites() {
  const { t, i18n } = useTranslation();
  const [news, setNews] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNews, setSelectedNews] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [newsRes, mediaRes, albumsRes] = await Promise.all([
        axios.get(`${API}/news`), axios.get(`${API}/media`), axios.get(`${API}/media/albums`)
      ]);
      setNews(newsRes.data); setMedia(mediaRes.data); setAlbums(albumsRes.data);
    } catch (error) { console.error('Error fetching data:', error); }
    finally { setLoading(false); }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

  const formatTime = (dateStr) => {
    const diffDays = Math.floor(Math.abs(new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Aujourd'hui"; if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`; return formatDate(dateStr);
  };

  const isYoutubeUrl = (url) => url && (url.includes('youtube.com') || url.includes('youtu.be'));
  const getYoutubeEmbedUrl = (url) => {
    if (url.includes('youtu.be/')) return `https://www.youtube.com/embed/${url.split('youtu.be/')[1].split('?')[0]}`;
    if (url.includes('watch?v=')) return `https://www.youtube.com/embed/${url.split('watch?v=')[1].split('&')[0]}`;
    return url;
  };
  const getMediaUrl = (url) => url.startsWith('/api/uploads/') ? `${window.location.origin}${url}` : url;

  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="actualites-page">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-fuchsia-50 via-white to-lime-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">Restez Informés</span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-zinc-900 mb-6">{t('actualites.title')}</h1>
          <p className="text-zinc-600 max-w-2xl mx-auto text-lg">{t('actualites.heroDesc')}</p>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/endurance" className="block mb-8 p-4 bg-gradient-to-r from-fuchsia-600 to-pink-600 rounded-xl text-white hover:from-fuchsia-700 hover:to-pink-700 transition-all group" data-testid="endurance-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center"><Trophy className="h-6 w-6" /></div>
                <div><h3 className="font-bold text-lg">Endurance Équestre</h3><p className="text-white/80 text-sm">Règlements officiels & Résultats des courses</p></div>
              </div>
              <ArrowRight className="h-6 w-6 group-hover:translate-x-2 transition-transform" />
            </div>
          </Link>

          <Tabs defaultValue="news" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 gap-2 bg-transparent h-auto mb-8">
              <TabsTrigger value="news" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-zinc-100 py-3 rounded-lg">Articles</TabsTrigger>
              <TabsTrigger value="gallery" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-zinc-100 py-3 rounded-lg">Galerie Médias</TabsTrigger>
            </TabsList>

            {/* News Tab */}
            <TabsContent value="news" data-testid="news-tab">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {[1, 2, 3].map((i) => (<Card key={i} className="overflow-hidden"><Skeleton className="h-48 w-full" /><CardContent className="p-6"><Skeleton className="h-6 w-3/4 mb-4" /><Skeleton className="h-4 w-full mb-2" /><Skeleton className="h-4 w-2/3" /></CardContent></Card>))}
                </div>
              ) : news.length === 0 ? (
                <div className="text-center py-16" data-testid="no-news-message"><p className="text-zinc-500 text-lg mb-4">{t('actualites.noNews')}</p></div>
              ) : (
                <>
                  {news.length > 0 && (
                    <Card className="overflow-hidden mb-12 group border-0 shadow-xl" data-testid="featured-news">
                      <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="relative h-64 lg:h-auto overflow-hidden"><img src={news[0].image_url || 'https://images.unsplash.com/photo-1591341066369-3536f913190b?w=800'} alt={news[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                        <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                          <div className="flex items-center gap-2 text-sm text-zinc-500 mb-4"><Clock className="h-4 w-4 text-fuchsia-500" /><span>{formatTime(news[0].published_at)}</span></div>
                          <h2 className="font-['Playfair_Display'] text-2xl lg:text-3xl font-bold text-zinc-900 mb-4 group-hover:text-fuchsia-600 transition-colors">{news[0].title}</h2>
                          <p className="text-zinc-600 mb-6 leading-relaxed">{news[0].excerpt}</p>
                          <Button variant="outline" className="self-start rounded-full border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50" onClick={() => setSelectedNews(news[0])} data-testid="read-more-btn-featured">Lire la suite<ArrowRight className="ml-2 h-4 w-4" /></Button>
                        </CardContent>
                      </div>
                    </Card>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {news.slice(1).map((item, index) => (
                      <Card key={item.id} className="overflow-hidden group hover:-translate-y-2 transition-all duration-300 border-0 shadow-lg" data-testid={`news-card-${index}`}>
                        <div className="relative h-48 overflow-hidden">
                          <OptimizedImage src={item.image_url || 'https://images.unsplash.com/photo-1591341066369-3536f913190b?w=800'} alt={item.title} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
                          <div className="absolute bottom-4 left-4 flex items-center gap-2 text-white/90 text-sm bg-black/50 rounded-full px-3 py-1"><Calendar className="h-4 w-4" /><span>{formatDate(item.published_at)}</span></div>
                        </div>
                        <CardContent className="p-6">
                          <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-3 group-hover:text-fuchsia-600 transition-colors line-clamp-2">{item.title}</h3>
                          <p className="text-zinc-500 text-sm mb-4 line-clamp-3">{item.excerpt}</p>
                          <Button variant="ghost" className="p-0 h-auto text-fuchsia-600 hover:text-fuchsia-700" onClick={() => setSelectedNews(item)}>{t('actualites.readMore')}<ArrowRight className="ml-1 h-4 w-4" /></Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" data-testid="gallery-tab">
              {media.length === 0 ? (
                <div className="text-center py-16">
                  <p className="text-zinc-500 text-lg">Aucun média pour le moment.</p>
                </div>
              ) : (
                <>
                  <AlbumGallery media={media} albums={albums} selectedAlbum={selectedAlbum} setSelectedAlbum={setSelectedAlbum} lightboxIndex={lightboxIndex} setLightboxIndex={setLightboxIndex} isAdmin={false} handleDeleteMedia={() => {}} getMediaUrl={getMediaUrl} />
                  {/* Loose Media (no album) */}
                  {!selectedAlbum && media.filter(m => !m.album).length > 0 && (
                    <div>
                      {albums.length > 0 && <h3 className="text-xl font-bold text-zinc-900 mb-4">Autres médias</h3>}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {media.filter(m => !m.album).map((item, index) => (
                          <Card key={item.id} className="overflow-hidden group border-0 shadow-lg" data-testid={`media-${index}`}>
                            <div className="relative aspect-video overflow-hidden bg-zinc-100">
                              {item.media_type === 'video' && isYoutubeUrl(item.url) ? (
                                <iframe src={getYoutubeEmbedUrl(item.url)} title={item.title} className="w-full h-full" allowFullScreen />
                              ) : item.media_type === 'video' ? (
                                <video src={getMediaUrl(item.url)} controls className="w-full h-full object-cover" />
                              ) : (
                                <img src={getMediaUrl(item.url)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                              )}
                              <div className="absolute top-3 left-3">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${item.media_type === 'video' ? 'bg-red-500 text-white' : 'bg-lime-500 text-white'}`}>
                                  {item.media_type === 'video' ? <><Video className="h-3 w-3 inline mr-1" />Vidéo</> : <><Image className="h-3 w-3 inline mr-1" />Photo</>}
                                </span>
                              </div>
                            </div>
                            <CardContent className="p-4">
                              <h3 className="font-bold text-zinc-900 mb-1">{item.title}</h3>
                              {item.description && <p className="text-sm text-zinc-500">{item.description}</p>}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <NewsletterSection />
      <NewsModal selectedNews={selectedNews} onClose={() => setSelectedNews(null)} formatDate={formatDate} />
    </div>
  );
}
